import { useParams } from "react-router-dom";
import { AppPageWrapper } from "@shared/ui";
import {
  useAttendees,
  useEvents,
  useTransactions,
} from "@features/events/hooks/useEventsDashboard";
import {
  Page,
  BlockStack,
  Text,
  MediaCard,
  VideoThumbnail,
  InlineStack,
  Link,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import type { Attendee, Event, Payment } from "@features/events/types";
import { EventStatusType } from "@features/events/types";
import LatestAttendees from "./LatestAttendees";
import UpcomingEventsCard from "./UpcomingEventsCard";
import TestDataCard from "./TestDataCard";
import LatestTransactions from "./LatestTransactions";

export default function EventsDashboardPage() {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{ eventOrganizerId: string }>();
  const eventsListDefaultUrl = hcc
    ? `/e/${hcc}/events?tab=upcoming&sort=starting+desc`
    : "";
  const transactionsListDefaultUrl = hcc ? `/e/${hcc}/transactions` : "";
  const { data: upcomingEvents = [], isLoading: upcomingEventsLoading } = useEvents(
    hcc,
    [EventStatusType.UPCOMING],
    "starting asc",
    5
  ) as { data: Event[]; isLoading: boolean };
  const { data: transactions = [], isLoading: transactionsLoading } = useTransactions(
    hcc,
    5
  ) as { data: Payment[]; isLoading: boolean };
  const { data: attendees = [], isLoading: attendeesLoading } = useAttendees(
    hcc,
    5
  ) as { data: Attendee[]; isLoading: boolean };

  return (
    <AppPageWrapper>
      <Page>
        {/* Main Page Title */}
        <BlockStack gap="600">
          {/* Test Data Card - only visible for test companies */}
          {hcc && <TestDataCard hcc={hcc} defaultCount={10} />}

          {/* Upcoming Events Section */}
          <BlockStack gap="300">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="headingLg" as="h2">
                {t("eventDashboard.upcomingEvents")}
              </Text>
              {!upcomingEventsLoading &&
                upcomingEvents.length === 5 &&
                Boolean(eventsListDefaultUrl) && (
                <Link url={eventsListDefaultUrl} removeUnderline>
                  {t("eventDashboard.seeAll")}
                </Link>
              )}
            </InlineStack>
            <UpcomingEventsCard hcc={hcc} />
          </BlockStack>

          {/* Latest Transactions Section */}
          {!transactionsLoading && transactions.length === 0 ? (
            <></>
          ) : (
            <BlockStack gap="300">
              <InlineStack align="space-between" blockAlign="center">
                <Text variant="headingLg" as="h2">
                  {t("eventTransactions.title")}
                </Text>
                {!transactionsLoading &&
                  transactions.length === 5 &&
                  Boolean(transactionsListDefaultUrl) && (
                  <Link url={transactionsListDefaultUrl} removeUnderline>
                    {t("eventDashboard.seeAll")}
                  </Link>
                )}
              </InlineStack>
              <LatestTransactions
                transactions={transactions}
                loading={transactionsLoading}
              />
            </BlockStack>
          )}

          {/* Latest Registrations Section */}
          {!attendeesLoading && attendees.length === 0 ? (
            <></>
          ) : (
            <BlockStack gap="300">
              <Text variant="headingLg" as="h2">
                {t("eventDashboard.latestRegistrations")}
              </Text>
              <LatestAttendees
                attendees={attendees}
                loading={attendeesLoading}
              />
            </BlockStack>
          )}

          {/* Tutorial Section */}
          <MediaCard
            title={t("eventDashboard.tutorial.title")}
            primaryAction={{
              content: t("eventDashboard.tutorial.play"),
              onAction: () => {},
            }}
            description={t("eventDashboard.tutorial.desc")}
          >
            <VideoThumbnail
              videoLength={330}
              thumbnailUrl="https://api-intervention.medentic.app/api/public/img/wf/companies/01934e04-8d72-7216-96e4-6a7a928fa966/patients/019523a7-3efd-71f8-a55c-6cb669370fd1/_avatar/01963d170525732eb0974f6f1fa7bb56_max_800x800.jpeg"
              onClick={() => {}}
            />
          </MediaCard>
        </BlockStack>
      </Page>
    </AppPageWrapper>
  );
}
